import React, { useEffect, useMemo, useState, useContext } from 'react';

import { useTranslation } from 'react-i18next';
import Modal from 'react-bootstrap/Modal';
import 'bootstrap/dist/css/bootstrap.min.css';

import GroupedMultiDropdown from '../../components/Form/GroupedMultiDropdown';
import Textarea from '../../components/Form/Textarea';
import Button from '../../components/Button';
import { UiContext } from '../../context/UiContext';
import AdminService from '../../services/AdminService';
import classes from './styles.module.scss';
import Stars from '../FeedbackModal/Stars';

export default function RejectCandidateModal({
  show,
  handleClose,
  currentCandidate,
  refetch,
}) {
  const { t } = useTranslation();
  const { setIsFetching, showModal } = useContext(UiContext);

  const [reason, setReason] = useState([]);
  const [reasonNote, setReasonNote] = useState('');
  const [touched, setTouched] = useState(false);
  const [interviewNote, setInterviewNote] = useState('');
  const [interviewRating, setInterviewRating] = useState(0);

  const rejectionReasonOptions = useMemo(
    () => [
      {
        label: t(
          'modals.RejectCandidateModal.reason.reasons.Lacks required technical skills'
        ),
        value: 'Lacks required technical skills',
        disabled: false,
        categoryName: t(
          'modals.RejectCandidateModal.reason.categories.Skill/Qualification'
        ),
      },
      {
        label: t(
          'modals.RejectCandidateModal.reason.reasons.Lacks role-specific experience'
        ),
        value: 'Lacks role-specific experience',
        disabled: false,
        categoryName: t(
          'modals.RejectCandidateModal.reason.categories.Skill/Qualification'
        ),
      },
      {
        label: t(
          'modals.RejectCandidateModal.reason.reasons.Overqualified for the position'
        ),
        value: 'Overqualified for the position',
        disabled: false,
        categoryName: t(
          'modals.RejectCandidateModal.reason.categories.Skill/Qualification'
        ),
      },
      {
        label: t(
          'modals.RejectCandidateModal.reason.reasons.Does not meet certification/education criteria'
        ),
        value: 'Does not meet certification/education criteria',
        disabled: false,
        categoryName: t(
          'modals.RejectCandidateModal.reason.categories.Skill/Qualification'
        ),
      },
      {
        label: t(
          'modals.RejectCandidateModal.reason.reasons.Cultural fit concern'
        ),
        value: 'Cultural fit concern',
        disabled: false,
        categoryName: t(
          'modals.RejectCandidateModal.reason.categories.Fit/Behavioral'
        ),
      },
      {
        label: t(
          'modals.RejectCandidateModal.reason.reasons.Communication not strong'
        ),
        value: 'Communication not strong',
        disabled: false,
        categoryName: t(
          'modals.RejectCandidateModal.reason.categories.Fit/Behavioral'
        ),
      },
      {
        label: t(
          'modals.RejectCandidateModal.reason.reasons.Team compatibility issue'
        ),
        value: 'Team compatibility issue',
        disabled: false,
        categoryName: t(
          'modals.RejectCandidateModal.reason.categories.Fit/Behavioral'
        ),
      },
      {
        label: t(
          'modals.RejectCandidateModal.reason.reasons.Salary expectations exceed budget'
        ),
        value: 'Salary expectations exceed budget',
        disabled: false,
        categoryName: t(
          'modals.RejectCandidateModal.reason.categories.Availability/Expectation'
        ),
      },
      {
        label: t(
          'modals.RejectCandidateModal.reason.reasons.Availability or joining timeline not suitable'
        ),
        value: 'Availability or joining timeline not suitable',
        disabled: false,
        categoryName: t(
          'modals.RejectCandidateModal.reason.categories.Availability/Expectation'
        ),
      },
      {
        label: t(
          'modals.RejectCandidateModal.reason.reasons.Interview no-show'
        ),
        value: 'Interview no-show',
        disabled: false,
        categoryName: t(
          'modals.RejectCandidateModal.reason.categories.Process/Other'
        ),
      },
      {
        label: t(
          'modals.RejectCandidateModal.reason.reasons.Duplicate submission'
        ),
        value: 'Duplicate submission',
        disabled: false,
        categoryName: t(
          'modals.RejectCandidateModal.reason.categories.Process/Other'
        ),
      },
      {
        label: t(
          'modals.RejectCandidateModal.reason.reasons.Other (share details below)'
        ),
        value: 'Other (share details below)',
        disabled: false,
        categoryName: t(
          'modals.RejectCandidateModal.reason.categories.Process/Other'
        ),
      },
    ],
    [t]
  );

  useEffect(() => {
    if (!show) {
      setReason([]);
      setReasonNote('');
      setInterviewNote('');
      setInterviewRating(0);
      setTouched(false);
    }
  }, [show]);

  const hasOtherSelected =
    Array.isArray(reason) &&
    reason.some((r) => r.value.toLowerCase().includes('other'));

  const isReasonNoteRequired = hasOtherSelected;

  const isSubmitEnabled =
    (Array.isArray(reason) ? reason.length > 0 : reason) &&
    (!isReasonNoteRequired || reasonNote) &&
    interviewNote &&
    interviewRating;

  const onSubmit = async () => {
    setTouched(true);
    if (!isSubmitEnabled) return;

    showModal({
      title: t('modals.RejectCandidateModal.confirmTitle'),
      text: t('modals.RejectCandidateModal.confirmText'),
      dismissButtonLabel: t('common.no'),
      confirmButtonLabel: t('common.yes'),
      onConfirm: async () => {
        try {
          setIsFetching(true);
          const payload = {
            clientRejectionReason: Array.isArray(reason)
              ? reason.map((r) => r.value).join(', ')
              : reason,
            clientRejectionCover: reasonNote,
            clientInterviewRating: interviewRating,
            clientInterviewCover: interviewNote,
            id: currentCandidate?.id,
          };

          await AdminService.rejectCandidate(payload);
          await refetch();
        } catch (error) {
          console.log(error);
        } finally {
          setIsFetching(false);
          handleClose();
        }
      },
    });
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      className={classes.SubmitCandidateModal}
      backdropClassName={classes.backdrop}
      contentClassName={classes.modalContent}
      dialogClassName={classes.dialog}
    >
      <div className={classes.content}>
        <header className={classes.header}>
          <h2>{t('modals.RejectCandidateModal.title')}</h2>
          <div className={classes.candidateInfo}>
            <h3>
              {currentCandidate?.user?.userProfile?.firstName}{' '}
              {currentCandidate?.user?.userProfile?.lastName}
            </h3>
            <span>{currentCandidate?.jobPost?.jobTitle}</span>
          </div>
        </header>

        <div className={classes.form}>
          <div className={classes.row}>
            <GroupedMultiDropdown
              label={t('modals.RejectCandidateModal.reason.label')}
              options={rejectionReasonOptions}
              value={reason}
              placeholder={t('modals.RejectCandidateModal.reason.placeholder')}
              height={45}
              width="100%"
              setFieldValue={(_name, option) => setReason(option)}
              touched={touched}
              error={
                touched &&
                (Array.isArray(reason) ? reason.length === 0 : !reason)
                  ? t('common.required')
                  : null
              }
            />
          </div>

          <div className={classes.row}>
            <div className={classes.textareaContainerFull}>
              <Textarea
                label={t('modals.RejectCandidateModal.reasonNote.label')}
                note={
                  !isReasonNoteRequired
                    ? t('modals.RejectCandidateModal.reasonNote.optional')
                    : ''
                }
                height={80}
                placeholder={t(
                  'modals.RejectCandidateModal.reasonNote.placeholder'
                )}
                maxLength={5000}
                value={reasonNote}
                onChange={(e) => setReasonNote(e.target.value)}
                touched={touched}
                error={
                  touched && isReasonNoteRequired && !reasonNote
                    ? t('common.required')
                    : null
                }
              />
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.rowHeader}>
              <h3>{t('modals.RejectCandidateModal.interviewNote.label')}</h3>
              <Stars
                stars={interviewRating}
                setStars={(value) => setInterviewRating(value)}
              />
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.textareaContainerFull}>
              <Textarea
                height={80}
                placeholder={t(
                  'modals.RejectCandidateModal.interviewNote.placeholder'
                )}
                maxLength={5000}
                value={interviewNote}
                onChange={(e) => setInterviewNote(e.target.value)}
              />
            </div>
          </div>

          <div className={classes.buttons}>
            <Button
              width={250}
              height={52}
              disabled={!isSubmitEnabled}
              onClick={onSubmit}
            >
              {t('common.reject')}
            </Button>
            <Button
              width={250}
              height={52}
              variant="blackWhite"
              onClick={handleClose}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
